import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GuestHashService } from './guest-hash.service';

export interface UserState {
  isAuthenticated: boolean;
  userId: string | null;
  recoveryCode: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  private userStateSubject = new BehaviorSubject<UserState>({
    isAuthenticated: false,
    userId: null,
    recoveryCode: null,
  });

  public userState$: Observable<UserState> = this.userStateSubject.asObservable();

  constructor(private guestHashService: GuestHashService) {}

  /**
   * Initialize user state based on authentication status and local storage
   */
  initializeUserState(): void {
    const cidaasToken = localStorage.getItem('cidaas_token');

    if (cidaasToken) {
      // Authenticated user
      try {
        const decodedToken = this.decodeToken(cidaasToken);
        this.setUserState({
          isAuthenticated: true,
          userId: decodedToken.sub,
          recoveryCode: null,
        });
      } catch (error) {
        console.error('Failed to decode Cidaas token', error);
        this.initializeAnonymousUser();
      }
    } else {
      // Anonymous user
      this.initializeAnonymousUser();
    }
  }

  /**
   * Initialize anonymous user with device ID and recovery code
   */
  private initializeAnonymousUser(): void {
    // Get userId from GuestHashService (uses user.id from localStorage user object)
    const user = this.guestHashService.getUser();
    const userId = user.id;

    const recoveryCode = localStorage.getItem('recovery_code') || null;

    this.setUserState({
      isAuthenticated: false,
      userId: userId,
      recoveryCode: recoveryCode,
    });
  }

  /**
   * Update user state after Cidaas login
   */
  handleCidaasLogin(token: string): void {
    localStorage.setItem('cidaas_token', token);
    try {
      const decodedToken = this.decodeToken(token);
      this.setUserState({
        isAuthenticated: true,
        userId: decodedToken.sub,
        recoveryCode: null,
      });
    } catch (error) {
      console.error('Failed to decode Cidaas token', error);
    }
  }

  /**
   * Handle logout - revert to anonymous user
   */
  handleLogout(): void {
    localStorage.removeItem('cidaas_token');
    this.initializeAnonymousUser();
  }

  /**
   * Update recovery code in state and localStorage
   */
  setRecoveryCode(code: string): void {
    const currentState = this.userStateSubject.value;
    localStorage.setItem('recovery_code', code);
    this.setUserState({
      ...currentState,
      recoveryCode: code,
    });
  }

  /**
   * Get current user state synchronously
   */
  getCurrentUserState(): UserState {
    return this.userStateSubject.value;
  }

  /**
   * Private helper to set user state
   */
  private setUserState(state: UserState): void {
    this.userStateSubject.next(state);
  }

  /**
   * Generate UUID v4
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Simple JWT decoder (doesn't verify signature)
   */
  private decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }
      const decoded = JSON.parse(atob(parts[1]));
      return decoded;
    } catch (error) {
      throw new Error('Failed to decode token');
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.userStateSubject.value.isAuthenticated;
  }

  /**
   * Get current userId
   */
  getUserId(): string | null {
    return this.userStateSubject.value.userId;
  }

  /**
   * Get current recovery code
   */
  getRecoveryCode(): string | null {
    return this.userStateSubject.value.recoveryCode;
  }

  /**
   * Clear recovery code from state and storage
   */
  clearRecoveryCode(): void {
    const currentState = this.userStateSubject.value;
    localStorage.removeItem('recovery_code');
    this.setUserState({
      ...currentState,
      recoveryCode: null,
    });
  }

  /**
   * Clear all user data for migration
   */
  clearUserData(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('recovery_code');
    this.initializeUserState();
  }
}
